import { authClient } from "@/auth/auth-client";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { FieldError, FieldGroup } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { revalidateLogic, useForm } from "@tanstack/react-form";
import { Link, useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import z from "zod";

const forgotSchema = z.object({
  email: z.string().email("Geçerli bir email adresi girin"),
});

const resetSchema = z
  .object({
    password: z.string().min(8, "Şifre en az 8 karakter olmalı"),
    confirm: z.string().min(1, "Şifre tekrarı gerekli"),
  })
  .refine((v) => v.password === v.confirm, {
    message: "Şifreler uyuşmuyor",
    path: ["confirm"],
  });

function ForgotPasswordForm() {
  const [sent, setSent] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm({
    defaultValues: { email: "" },
    validationLogic: revalidateLogic(),
    validators: { onDynamic: forgotSchema },
    onSubmit: async ({ value }) => {
      setServerError(null);
      const { error } = await authClient.requestPasswordReset({
        email: value.email,
        redirectTo: `${window.location.origin}/password/change`,
      });
      if (error) {
        setServerError(error.message || "Bir hata oluştu.");
      } else {
        setSent(true);
      }
    },
  });

  if (sent) {
    return (
      <Card className="w-full">
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Email Gönderildi</CardTitle>
          <CardDescription>
            Şifre sıfırlama bağlantısı email adresinize gönderildi. Lütfen gelen
            kutunuzu kontrol edin.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <Link
            to="/password/login"
            className="text-muted-foreground text-sm underline underline-offset-4 hover:text-primary"
          >
            Giriş sayfasına dön
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader className="text-center">
        <CardTitle className="text-xl">Şifremi Unuttum</CardTitle>
        <CardDescription>
          Email adresinizi girin, şifre sıfırlama bağlantısı gönderelim
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <FieldGroup className="gap-4">
            <form.Field name="email">
              {(field) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="ornek@email.com"
                    autoComplete="email"
                    autoFocus
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </div>
              )}
            </form.Field>

            {serverError && (
              <p className="text-destructive text-sm">{serverError}</p>
            )}

            <form.Subscribe selector={(s) => s.isSubmitting}>
              {(isSubmitting) => (
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Gönderiliyor...
                    </>
                  ) : (
                    "Sıfırlama Bağlantısı Gönder"
                  )}
                </Button>
              )}
            </form.Subscribe>

            <p className="text-center text-muted-foreground text-sm">
              <Link
                to="/password/login"
                className="text-foreground underline underline-offset-4 hover:text-primary"
              >
                Giriş sayfasına dön
              </Link>
            </p>
          </FieldGroup>
        </form>
      </CardContent>
    </Card>
  );
}

function NewPasswordForm({ token }: { token: string }) {
  const [serverError, setServerError] = useState<string | null>(null);
  const navigate = useNavigate();

  const form = useForm({
    defaultValues: { password: "", confirm: "" },
    validationLogic: revalidateLogic(),
    validators: { onDynamic: resetSchema },
    onSubmit: async ({ value }) => {
      setServerError(null);
      const { error } = await authClient.resetPassword({
        newPassword: value.password,
        token,
      });
      if (error) {
        setServerError(
          error.message || "Şifre güncellenirken bir hata oluştu.",
        );
      } else {
        await navigate({ to: "/password/login" });
      }
    },
  });

  return (
    <Card className="w-full">
      <CardHeader className="text-center">
        <CardTitle className="text-xl">Yeni Şifre Belirle</CardTitle>
        <CardDescription>Hesabınız için yeni bir şifre girin</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <FieldGroup className="gap-4">
            <form.Field name="password">
              {(field) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="password">Yeni Şifre</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    autoFocus
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </div>
              )}
            </form.Field>

            <form.Field name="confirm">
              {(field) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="confirm">Şifre Tekrar</Label>
                  <Input
                    id="confirm"
                    type="password"
                    autoComplete="new-password"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </div>
              )}
            </form.Field>

            {serverError && (
              <p className="text-destructive text-sm">{serverError}</p>
            )}

            <form.Subscribe selector={(s) => s.isSubmitting}>
              {(isSubmitting) => (
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Güncelleniyor...
                    </>
                  ) : (
                    "Şifreyi Güncelle"
                  )}
                </Button>
              )}
            </form.Subscribe>
          </FieldGroup>
        </form>
      </CardContent>
    </Card>
  );
}

export function ResetPasswordForm({ token }: { token?: string }) {
  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted/40 p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-6">
        <div className="flex flex-col items-center gap-2">
          <Link to="/">
            <img
              src="/lightLogo.webp"
              alt="Logo"
              className="h-20 w-20 dark:hidden"
            />
            <img
              src="/darkLogo.webp"
              alt="Logo"
              className="hidden h-20 w-20 dark:block"
            />
          </Link>
        </div>
        {token ? <NewPasswordForm token={token} /> : <ForgotPasswordForm />}
      </div>
    </div>
  );
}
